import { useState, useEffect, useRef, useCallback } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { PartySocket } from 'partysocket';
import {
  GameState,
  GamePhase,
  PlayerState,
  ServerMessage,
  ClientMessage,
  GameSettings,
  calculateScore,
  CATEGORY_XP_BASE,
  CATEGORY_XP_PER_POINT,
} from '@brainfart/shared';
import { JoinScreen } from './components/JoinScreen';
import { LobbyScreen } from './components/LobbyScreen';
import { QuestionScreen } from './components/QuestionScreen';
import { RevealScreen } from './components/RevealScreen';
import { LeaderboardScreen } from './components/LeaderboardScreen';
import { WaitingScreen } from './components/WaitingScreen';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ReactionBar, ReactionsLayer } from './components/Reactions';
import { addCategoryXP, recordRivalry } from './profileStore';
import { getAvatar } from './identity';
import { unlock } from './achievements';
import {
  trackGameCreated,
  trackGameJoined,
  trackGameStarted,
  trackGameEnded,
  trackQuestionAnswered,
  trackQuestionTimedOut,
  trackCategorySelected,
  trackThemeSelected,
  trackSettingsChanged,
  trackPlayerLeft,
  trackReactionSent,
  trackShareClicked,
  trackRematchStarted,
} from './telemetry';

declare global {
  interface ImportMetaEnv {
    readonly VITE_PARTYKIT_HOST: string;
  }
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

const PARTYKIT_HOST = import.meta.env.VITE_PARTYKIT_HOST || 'localhost:1999';
const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const PLAYER_ID_KEY = 'bf_player_id';
const SESSION_KEY = 'bf_session';

function generateRoomCode(): string {
  let code = '';
  for (let i = 0; i < 6; i++) code += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
  return code;
}

function getOrCreatePlayerId(): string {
  let id = localStorage.getItem('bf_player_id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('bf_player_id', id);
  }
  return id;
}

function getStoredSession(): { room: string; name: string } | null {
  try {
    const raw = localStorage.getItem('bf_session');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveSession(room: string, name: string) {
  localStorage.setItem('bf_session', JSON.stringify({ room, name }));
}

function clearSession() {
  localStorage.removeItem('bf_session');
}

interface AppState {
  phase: GamePhase;
  game: GameState | null;
  me: PlayerState | null;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  error: string | null;
  isHost: boolean;
  resuming: boolean;
}

interface AppProps {
  onPlaySolo: () => void;
  onPlayDuel: (category?: string) => void;
  onPlayDaily: () => void;
  onViewStats: () => void;
}

export function App({ onPlaySolo, onPlayDaily, onPlayDuel, onViewStats }: AppProps) {
  const [roomFromUrl, setRoomFromUrl] = useState(() => new URLSearchParams(window.location.search).get('room')?.toUpperCase() || '');
  const playerId = useRef(getOrCreatePlayerId()).current;

  const [state, setState] = useState<AppState>(() => {
    const session = getStoredSession();
    const canResume = Boolean(session && (!roomFromUrl || roomFromUrl === session.room));
    return {
      phase: 'lobby',
      game: null,
      me: null,
      connectionStatus: 'disconnected',
      error: null,
      isHost: false,
      resuming: canResume,
    };
  });

  const socketRef = useRef<PartySocket | null>(null);

  // Transient floating emoji reactions — kept out of the game-state reducer so
  // they never persist or re-trigger; each self-removes after its animation.
  const [reactions, setReactions] = useState<{ id: string; emoji: string; x: number }[]>([]);

  const handleServerMessage = useCallback((msg: ServerMessage) => {
    if (msg.type === 'reaction') {
      const id = crypto.randomUUID();
      const x = 8 + Math.random() * 84; // horizontal spawn position (vw %)
      setReactions(prev => [...prev, { id, emoji: msg.payload.emoji, x }]);
      setTimeout(() => setReactions(prev => prev.filter(r => r.id !== id)), 2600);
      return;
    }
    setState(prev => {
      const newState = { ...prev };

      switch (msg.type) {
        case 'joined':
        case 'state':
          newState.game = msg.payload.game;
          newState.me = msg.payload.you;
          newState.phase = msg.payload.game.phase;
          newState.isHost = msg.payload.you?.id === msg.payload.game.hostId;
          newState.resuming = false;
          break;

        case 'player-joined':
          if (newState.game) {
            const exists = newState.game.players.some(p => p.id === msg.payload.player.id);
            newState.game = {
              ...newState.game,
              players: exists
                ? newState.game.players.map(p => p.id === msg.payload.player.id ? { ...p, ...msg.payload.player } : p)
                : [...newState.game.players, msg.payload.player as PlayerState],
            };
          }
          break;

        case 'player-left':
          if (newState.game) {
            newState.game = {
              ...newState.game,
              players: newState.game.players.map(p =>
                p.id === msg.payload.playerId ? { ...p, connected: false } : p
              ),
            };
          }
          break;

        case 'phase-changed':
          newState.phase = msg.payload.phase;
          if (newState.game) newState.game = { ...newState.game, phase: msg.payload.phase };
          break;

        case 'round-started':
          if (newState.game) {
            const existingIdx = newState.game.rounds.findIndex(r => r.id === msg.payload.round.id);
            const isNewRound = existingIdx < 0;
            const rounds = isNewRound
              ? [...newState.game.rounds, msg.payload.round]
              : newState.game.rounds.map(r => r.id === msg.payload.round.id ? msg.payload.round : r);
            newState.game = {
              ...newState.game,
              currentRound: isNewRound ? rounds.length - 1 : existingIdx,
              rounds,
              players: isNewRound
                ? newState.game.players.map(p => ({ ...p, hasAnswered: false, answer: null, rank: null }))
                : newState.game.players,
            };
          }
          break;

        case 'round-ended':
          if (newState.game) {
            const round = msg.payload.round;
            newState.game = {
              ...newState.game,
              rounds: newState.game.rounds.map(r => r.id === round.id
                ? { ...r, state: round.state, endedAt: round.endedAt, rankings: round.rankings }
                : r),
              players: newState.game.players.map(p => {
                const scoreChange = msg.payload.scores[p.id] || 0;
                return { ...p, score: p.score + scoreChange };
              }),
            };
          }
          break;

        case 'answer-received':
          if (newState.game) {
            newState.game = {
              ...newState.game,
              players: newState.game.players.map(p =>
                p.id === msg.payload.playerId ? { ...p, hasAnswered: msg.payload.hasAnswered } : p
              ),
            };
          }
          break;

        case 'reveal-answer':
          if (newState.game) {
            const round = newState.game.rounds[newState.game.currentRound];
            if (round) {
              round.answers = { ...round.answers, [msg.payload.playerId]: msg.payload.answer };
              round.correctness = { ...round.correctness, [msg.payload.playerId]: msg.payload.correct };
              if (msg.payload.correctAnswer !== undefined) {
                round.question = { ...round.question, correctAnswer: msg.payload.correctAnswer };
              }
            }
          }
          break;

        case 'scores-updated':
          if (newState.game) {
            newState.game = {
              ...newState.game,
              players: newState.game.players.map(p => ({
                ...p,
                score: msg.payload.scores[p.id] !== undefined ? msg.payload.scores[p.id] : p.score,
              })),
            };
          }
          break;

        case 'settings-updated':
          if (newState.game) newState.game = { ...newState.game, settings: msg.payload };
          break;

        case 'error':
          newState.resuming = false;
          if (!newState.game) {
            // Failed join/create (room ended, full, already in progress, etc.)
            // — don't dead-end on a blocking error screen, just land them back
            // on the real homepage so they can jump into Daily/Solo/host a new
            // game instead of bouncing off a dead link.
            clearSession();
            window.history.replaceState(null, '', window.location.pathname);
            setRoomFromUrl('');
          } else {
            newState.error = msg.payload.message;
          }
          break;

        case 'kicked':
          newState.error = `Kicked: ${msg.payload.reason}`;
          clearSession();
          break;
      }

      return newState;
    });
  }, []);

  const connect = useCallback((room: string, name: string, initialMessage: ClientMessage) => {
    const socket = new PartySocket({ host: PARTYKIT_HOST, party: 'quiz-room', room, id: playerId });
    socketRef.current = socket;
    setState(prev => ({ ...prev, connectionStatus: 'connecting' }));

    let hasOpenedOnce = false;

    socket.onopen = () => {
      setState(prev => ({ ...prev, connectionStatus: 'connected', error: null }));
      if (!hasOpenedOnce) {
        hasOpenedOnce = true;
        socket.send(JSON.stringify(initialMessage));
      } else {
        socket.send(JSON.stringify({ type: 'join', payload: { name, code: room, avatar: getAvatar(name) } }));
      }
    };
    socket.onclose = () => setState(prev => ({ ...prev, connectionStatus: 'disconnected' }));
    socket.onerror = (e: Event) => {
      console.error('Socket error:', e);
      setState(prev => ({ ...prev, connectionStatus: 'error', error: 'Connection failed', resuming: false }));
    };
    socket.onmessage = (event: MessageEvent) => {
      try {
        handleServerMessage(JSON.parse(event.data) as ServerMessage);
      } catch (e) {
        console.error('Message parse error:', e);
      }
    };
  }, [handleServerMessage, playerId]);

  useEffect(() => {
    const session = getStoredSession();
    if (session && (!roomFromUrl || roomFromUrl === session.room)) {
      connect(session.room, session.name, { type: 'join', payload: { name: session.name, code: session.room, avatar: getAvatar(session.name) } });
    }
  }, []);

  // When a multiplayer game ends, bank category XP from my correct answers and
  // update W/L records against everyone else — once per game.
  // Each multiplayer screen (lobby, question, reveal, results) starts at the
  // top. The homepage is also phase 'lobby' (with no game yet), so key on both.
  const inGame = Boolean(state.game);
  useEffect(() => { window.scrollTo(0, 0); }, [state.phase, inGame]);

  const recordedEndRef = useRef(false);
  useEffect(() => {
    const { phase, game, me } = state;
    if (phase !== 'ended') {
      recordedEndRef.current = false;
      return;
    }
    if (!game || !me || !isParticipant(game, me.id)) return;
    if (recordedEndRef.current) return;
    recordedEndRef.current = true;

    const xp: Record<string, number> = {};
    for (const round of game.rounds) {
      if (round.question.type !== 'multiple-choice') continue;
      if (round.correctness?.[me.id]) {
        const cat = round.question.category;
        xp[cat] = (xp[cat] || 0) + Math.round(CATEGORY_XP_BASE + round.question.basePoints * CATEGORY_XP_PER_POINT);
      }
    }
    if (Object.keys(xp).length > 0) addCategoryXP(xp);

    const participants = game.players.filter(p => isParticipant(game, p.id));
    trackGameEnded(game.code, {
      roundCount: game.rounds.length,
      playerCount: participants.length,
      durationMs: (game.endedAt ?? Date.now()) - (game.startedAt ?? Date.now()),
      correct: game.rounds.filter(r => r.correctness?.[me.id]).length,
      rank: [...participants].sort((a, b) => b.score - a.score).findIndex(p => p.id === me.id) + 1,
      categories: [...new Set(game.rounds.map(r => r.question.category))],
    });

    // `me` is the snapshot from joining; the live score is on the game's player list.
    const myScore = game.players.find(p => p.id === me.id)?.score ?? me.score;
    for (const p of game.players) {
      if (p.id === me.id || !p.name || !isParticipant(game, p.id)) continue;
      const outcome = myScore > p.score ? 'win' : myScore < p.score ? 'loss' : 'tie';
      recordRivalry(p.name, outcome);
    }
  }, [state.phase]);

  const send = useCallback((msg: ClientMessage) => {
    socketRef.current?.send(JSON.stringify(msg));
  }, []);

  const actions = {
    createGame: (name: string, settings: Partial<GameSettings> = {}) => {
      const code = generateRoomCode();
      window.history.replaceState(null, '', `?room=${code}`);
      saveSession(code, name);
      trackGameCreated(code, settings.categories || [], settings.themes || [], true);
      connect(code, name, { type: 'create', payload: { name, settings, avatar: getAvatar(name) } });
      unlock('party_host');
    },

    joinGame: (name: string, code: string) => {
      const room = code.trim().toUpperCase();
      window.history.replaceState(null, '', `?room=${room}`);
      saveSession(room, name);
      trackGameJoined(room, 0); // playerCount will be known after join
      connect(room, name, { type: 'join', payload: { name, code: room, avatar: getAvatar(name) } });
    },

    startGame: () => {
      const { game } = state;
      if (game) {
        trackGameStarted(game.code, game.settings.rounds, game.players.length, game.settings.categories);
      }
      send({ type: 'start', payload: {} });
    },

    updateSettings: (settings: Partial<GameSettings>) => {
      const { game } = state;
      if (game) {
        trackSettingsChanged(game.code, settings);
        if (settings.categories) trackCategorySelected(game.code, settings.categories, 0);
        if (settings.themes) trackThemeSelected(game.code, settings.themes);
      }
      send({ type: 'update-settings', payload: settings });
    },

    submitAnswer: (roundId: string, answer: string) => {
      const { game } = state;
      if (game) {
        const round = game.rounds.find(r => r.id === roundId);
        if (round) {
          trackQuestionAnswered(game.code, round.question.id, round.question.category, null, Date.now() - (round.startedAt ?? Date.now()));
        }
      }
      send({ type: 'answer', payload: { roundId, answer } });
    },

    forceNext: (roundId: string) => send({ type: 'force-next', payload: { roundId } }),

    playAgain: () => {
      const { game } = state;
      if (game) trackRematchStarted(game.code, game.players.filter(p => p.connected).length);
      send({ type: 'play-again', payload: {} });
    },

    goHome: () => {
      const { game } = state;
      if (game) trackPlayerLeft(game.code, game.players.filter(p => p.connected).length);
      send({ type: 'leave', payload: {} });
      clearSession();
      window.location.href = window.location.pathname;
    },

    react: (emoji: string) => {
      const { game } = state;
      if (game) trackReactionSent(game.code, emoji);
      send({ type: 'react', payload: { emoji } });
    },
  };

  const renderScreen = () => {
    const { phase, game, me, isHost, error, resuming } = state;

    if (error) {
      return <ErrorScreen message={error} onDismiss={() => setState(s => ({ ...s, error: null }))} />;
    }

    if (!game || !me) {
      if (resuming) return <LoadingSpinner label="Reconnecting you to your game..." />;
      return <JoinScreen onCreate={actions.createGame} onJoin={actions.joinGame} initialRoom={roomFromUrl} onPlaySolo={onPlaySolo} onPlayDaily={onPlayDaily} onPlayDuel={onPlayDuel} onViewStats={onViewStats} />;
    }

    // Joined this link mid-game or after it ended: wait for the next game.
    if (phase !== 'lobby' && !isParticipant(game, me.id)) {
      const host = game.players.find(p => p.id === game.hostId);
      return <WaitingScreen game={game} hostName={host?.name} onLeave={actions.goHome} />;
    }

    // Late joiners sit in game.players but aren't part of this game.
    const current = { ...game, players: game.players.filter(p => isParticipant(game, p.id)) };
    const host = game.players.find(p => p.id === game.hostId);
    const canStartNext = isHost || !host?.connected;

    switch (phase) {
      case 'lobby':
        return <LobbyScreen game={game} me={me} isHost={isHost} onStart={actions.startGame} onUpdateSettings={actions.updateSettings} onLeaveHome={actions.goHome} />;

      case 'playing': {
        const round = game.rounds[game.currentRound];
        if (!round) return <LoadingSpinner label="Loading round..." />;

        return (
          <QuestionScreen
            key={round.id}
            round={round}
            game={current}
            me={me}
            onSubmit={actions.submitAnswer}
            onForceNext={actions.forceNext}
            isHost={isHost}
            onLeave={actions.goHome}
          />
        );
      }

      case 'revealing': {
        const round = game.rounds[game.currentRound];
        if (!round) return <LoadingSpinner label="Loading..." />;
        return (
          <RevealScreen
            key={round.id}
            round={round}
            game={current}
            me={me}
            onLeave={actions.goHome}
          />
        );
      }

      case 'final':
      case 'ended':
        return (
          <LeaderboardScreen
            game={current}
            me={me}
            final={phase === 'ended'}
            canStartNext={canStartNext}
            hostName={host?.name}
            onPlayAgain={actions.playAgain}
            onLeave={actions.goHome}
          />
        );

      default:
        return <LoadingSpinner label="Loading..." />;
    }
  };

  return (
    <div className="app">
      <AnimatePresence mode="wait">
        {renderScreen()}
      </AnimatePresence>

      {state.connectionStatus !== 'connected' && state.connectionStatus !== 'disconnected' && (
        <div className="connection-indicator">
          {state.connectionStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}
        </div>
      )}

      {/* Live emoji reactions: the bar is for the reveal (not while a question
          is live, when it only gets in the way); the floating overlay that
          renders everyone's is always on. */}
      {state.game && state.me && state.phase === 'revealing' && (
        <ReactionBar onReact={actions.react} />
      )}
      <ReactionsLayer reactions={reactions} />
    </div>
  );
}

function isParticipant(game: GameState, playerId: string): boolean {
  const ids = game.participantIds ?? [];
  return ids.length === 0 || ids.includes(playerId);
}

function ErrorScreen({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <m.div
      className="error-screen"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <div className="card" style={{ maxWidth: 400, textAlign: 'center', padding: '3rem' }}>
        <h1 style={{ color: '#FF4D6A', marginBottom: '1rem' }}>Quiz interrupted</h1>
        <p style={{ color: '#ccc', marginBottom: '2rem' }}>{message}</p>
        <button className="btn btn-primary" onClick={onDismiss}>Got it</button>
      </div>
    </m.div>
  );
}
